import Switch from "@/components/Switch";
import Divider from "@/components/Divider";
import TextField from "@/components/TextField";
import DateSelect from "@/components/DateSelect";
import Autocomplete from "@/components/Autocomplete";
import ListItem from "@/components/ListItem/ListItem";
import TeamVisibilityField from "@/features/global/components/AddTaskSideSheet/TeamVisibilityField";
import {
  privateTaskSupportingText,
  hasDistinctPrivateAssignee
} from "@/features/global/components/AddTaskSideSheet/privateTaskCopy";
import type { TeamVisibilityOption } from "@/features/global/components/AddTaskSideSheet/teamVisibilityOptions";
import type { DateValueType } from "react-tailwindcss-datepicker";
import type { BackofficeUser, AttachTaskErrors } from "@/hooks/useAttachTask";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";

interface AttachTaskSectionProps {
  attachTask: boolean;
  onToggle: (enabled: boolean) => void;
  taskName: string;
  onTaskNameChange: (value: string) => void;
  taskAssignee: BackofficeUser | null;
  assigneeSearchQuery: string;
  onAssigneeSearchChange: (value: string) => void;
  selectAssignee: (user: BackofficeUser) => void;
  clearAssignee: () => void;
  assigneeResults: BackofficeUser[];
  assigneeLoading: boolean;
  taskDueDate: DateValueType;
  onDueDateChange: (date: DateValueType) => void;
  taskIsPrivate: boolean;
  onTaskIsPrivateChange: (isPrivate: boolean) => void;
  errors: AttachTaskErrors;
  clearError: (field: keyof AttachTaskErrors) => void;
  disabled?: boolean;
  objectLabel?: string;
  selectedTeamTags?: string[];
  teamSearchTerm?: string;
  onTeamSearchChange?: (value: string) => void;
  availableDepartmentOptions?: TeamVisibilityOption[];
  onTeamVisibilitySelect?: (option: TeamVisibilityOption) => void;
  onRemoveTeamTag?: (department: string) => void;
}

export default function AttachTaskSection({
  attachTask,
  onToggle,
  taskName,
  onTaskNameChange,
  taskAssignee,
  assigneeSearchQuery,
  onAssigneeSearchChange,
  selectAssignee,
  clearAssignee,
  assigneeResults,
  assigneeLoading,
  taskDueDate,
  onDueDateChange,
  taskIsPrivate,
  onTaskIsPrivateChange,
  errors,
  clearError,
  disabled = false,
  objectLabel = "note",
  selectedTeamTags = [],
  teamSearchTerm = "",
  onTeamSearchChange = () => {},
  availableDepartmentOptions = [],
  onTeamVisibilitySelect = () => {},
  onRemoveTeamTag = () => {}
}: AttachTaskSectionProps) {
  const currentUser = useCurrentBackofficeUser();

  return (
    <>
      <Divider />
      <Switch
        disabled={disabled}
        headline="Attach a Task"
        selected={attachTask}
        supportingText={`Create a follow-up task linked to this ${objectLabel}`}
        onChange={() => onToggle(!attachTask)}
      />

      {attachTask && (
        <div className="space-y-6">
          <TextField
            label="Task Name"
            value={taskName}
            onChangeText={(value) => {
              onTaskNameChange(value);
              if (errors.taskName) clearError("taskName");
            }}
            error={!!errors.taskName}
            errorText={errors.taskName}
            className="w-full"
            disabled={disabled}
          />

          <Autocomplete<BackofficeUser>
            label="Assignee"
            disabled={disabled}
            query={taskAssignee ? taskAssignee.name : assigneeSearchQuery}
            loadingResults={assigneeLoading}
            data={assigneeResults}
            onChangeText={(value) => {
              if (taskAssignee) clearAssignee();
              onAssigneeSearchChange(value);
              if (errors.taskAssignee) clearError("taskAssignee");
            }}
            reset={clearAssignee}
            onSelect={(user: BackofficeUser) => {
              selectAssignee(user);
              if (errors.taskAssignee) clearError("taskAssignee");
            }}
            renderItem={(user: BackofficeUser) => (
              <ListItem compact useAvatar iconName="person" headline={user.name} supportingText={user.email} />
            )}
            error={!!errors.taskAssignee}
            errorText={errors.taskAssignee}
          />

          <TeamVisibilityField
            selectedTeamTags={selectedTeamTags}
            teamSearchTerm={teamSearchTerm}
            onTeamSearchChange={onTeamSearchChange}
            availableDepartmentOptions={availableDepartmentOptions}
            onSelect={onTeamVisibilitySelect}
            onRemove={onRemoveTeamTag}
            disabled={disabled}
            data-testid="attach-task-team-visibility-autocomplete"
          />

          <DateSelect
            label="Due Date"
            value={taskDueDate}
            onChange={(date) => onDueDateChange(typeof date === "string" ? null : date)}
            placeholder="Select a date"
            useSingleDate
            useModal
            className="w-full"
          />

          <Switch
            disabled={disabled}
            headline="Make Private"
            selected={taskIsPrivate}
            supportingText={privateTaskSupportingText({
              hasDistinctAssignee: hasDistinctPrivateAssignee(taskAssignee?.id, currentUser?.id),
              hasSelectedTeams: selectedTeamTags.length > 0
            })}
            onChange={() => onTaskIsPrivateChange(!taskIsPrivate)}
          />
        </div>
      )}
    </>
  );
}
